import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, getAllBrands, inviteUser, updateUserStatus } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { USER_ROLE, formatDate, initials, label } from '../../constants/garmentCodes';

const EMPTY_FORM = { name: '', email: '', mobileNumber: '', role: 2, password: '' };
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

export default function Users() {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.user.users);
  const loading = useSelector((state) => state.user.loading);
  const brands = useSelector((state) => state.brand.brands);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [brandIds, setBrandIds] = useState([]);

  useEffect(() => {
    dispatch(getAllBrands());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllUsers({ role: '', brandId: '' }));
  }, [dispatch]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleBrand = (id) => setBrandIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const invite = (e) => {
    e.preventDefault();
    const role = Number(form.role);
    dispatch(
      inviteUser({
        payload: {
          name: form.name.trim(),
          email: form.email.trim(),
          mobileNumber: form.mobileNumber || null,
          role,
          brandIds,
        },
      }),
    );
    setForm(EMPTY_FORM);
    setBrandIds([]);
    setInviteOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Header title="Users & Roles" breadcrumbs={[{ workspace: true }, { label: 'Users & Roles' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="ml-auto flex items-center gap-3">
            {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
            <button onClick={() => setInviteOpen(true)} className="px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
              + Invite User
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-[11px] font-semibold text-gray-700 mb-2">Manufacturer Admin</p>
            <p className="text-[12px] text-gray-500 leading-relaxed">Full manufacturer access — wallet, users, settings, approvals, overrides, reversals, reconciliation.</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <p className="text-[11px] font-semibold text-gray-700 mb-2">Manufacturer Coordinator</p>
            <p className="text-[12px] text-gray-500 leading-relaxed">Operational access — upload fabrics, designs, update tasks, add comments. Cannot manage users, wallet rules, or post reversals.</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Users"
            columns={[
              { id: 'name', label: 'NAME', align: 'left' },
              { id: 'email', label: 'EMAIL', align: 'left' },
              { id: 'role', label: 'ROLE', align: 'left' },
              { id: 'brands', label: 'ASSIGNED BRANDS', align: 'left' },
              { id: 'lastLogin', label: 'LAST LOGIN', align: 'left' },
              { id: '2fa', label: '2FA', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'actions', label: 'ACTIONS', align: 'left', disableFilter: true },
            ]}
            rows={users.map((user) => {
              const role = label(USER_ROLE, user.role);
              const assigned = user.brands || [];
              const active = user.status !== 'Suspended';
              return {
                id: user.id,
                rColumns: [
                  <div key="name" label={user.name || ''} className={`flex items-center gap-3 w-full h-full px-2 py-1 ${user.__optimistic ? 'opacity-60' : ''}`}>
                    <div className="w-7 h-7 bg-[#1b3a6b] rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                      {user.avatar || initials(user.name)}
                    </div>
                    <span className="text-[12px] font-medium text-gray-800">{user.name}</span>
                  </div>,
                  <div key="email" label={user.email || ''} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-600">{user.email}</div>,
                  <div key="role" label={role} className="flex items-center w-full h-full px-2 py-1">
                    <span className={`text-[11px] font-medium px-2 py-1 rounded ${role === 'Manufacturer Admin' ? 'bg-[#1b3a6b]/10 text-[#1b3a6b]' : 'bg-gray-100 text-gray-600'}`}>{role}</span>
                  </div>,
                  <div key="brands" label={assigned.join(', ')} className="flex items-center gap-1 flex-wrap w-full h-full px-2 py-1">
                    {assigned.map((b) => <span key={b} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{b}</span>)}
                  </div>,
                  <div key="login" label={user.lastLogin || ''} className="flex items-center w-full h-full px-2 py-1 text-[12px] text-gray-500">{formatDate(user.lastLogin)}</div>,
                  <div key="2fa" label={user.twoFactor ? 'Enabled' : '—'} className="flex items-center w-full h-full px-2 py-1">
                    {user.twoFactor ? <span className="text-[11px] text-green-600 font-medium">Enabled</span> : <span className="text-[11px] text-gray-400">—</span>}
                  </div>,
                  <div key="stat" label={user.status || ''} className="flex items-center w-full h-full px-2 py-1"><Badge status={user.status || '—'} /></div>,
                  <div key="act" label="" className="flex items-center gap-2 w-full h-full px-2 py-1">
                    {!user.__optimistic && (
                      <>
                        <button
                          onClick={() => dispatch(updateUserStatus({ id: user.id, status: active ? 'Suspended' : 'Active' }))}
                          className={`text-[11px] hover:underline ${active ? 'text-red-500' : 'text-green-600'}`}
                        >
                          {active ? 'Suspend' : 'Reactivate'}
                        </button>
                      </>
                    )}
                  </div>,
                ],
              };
            })}
          />
          {!loading && users.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No users</p>}
        </div>
      </main>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite User" size="md">
        <form onSubmit={invite} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Full Name *</label>
              <input required value={form.name} onChange={set('name')} placeholder="Full name" className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Email *</label>
              <input required type="email" value={form.email} onChange={set('email')} placeholder="name@company.in" className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Mobile</label>
              <input value={form.mobileNumber} onChange={set('mobileNumber')} placeholder="+91 98765 00000" className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Role *</label>
              <select value={form.role} onChange={set('role')} className={inputCls}>
                <option value={2}>Manufacturer Coordinator</option>
                <option value={1}>Manufacturer Admin</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Assign Brands</label>
            <div className="flex gap-3 flex-wrap">
              {brands.length === 0 && <span className="text-[12px] text-gray-400">No brands yet</span>}
              {brands.map((b) => (
                <label key={b.id} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" className="rounded" checked={brandIds.includes(b.id)} onChange={() => toggleBrand(b.id)} />
                  <span className="text-[12px] text-gray-700">{b.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-[12px] text-blue-800">
            {Number(form.role) === 2 ? (
              <><strong>Coordinator role:</strong> can view assigned brands, upload fabrics/designs, update tasks, add comments. Cannot manage users, wallet rules, or settings.</>
            ) : (
              <><strong>Admin role:</strong> full access to manage users, brands, orders, wallet rules, settings, and all production operations.</>
            )} 
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setInviteOpen(false)} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Send Invitation</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
